<template>
  <a-modal
      v-model:visible="visible"
      title="项目工单详情"
      @cancel="handleCancel"
      width="1600px"
      :footer="false"
      :draggable="false"
  >
    <div class="work-order-info-modal-content">
      <div class="modal-header-actions" v-if="workOrderData">
        <!-- 添加编辑按钮 -->
        <a-button 
          type="primary" 
          status="warning" 
          @click="showEditModal"
          style="margin-right: 10px;">
          <template #icon>
            <icon-edit/>
          </template>
          编辑
        </a-button>
        <!-- 添加审核按钮 -->
        <a-button 
          type="primary" 
          status="warning" 
          @click="showApprovalModal"
          :disabled="approveButtonStatus(workOrderData)"
          style="margin-right: 10px;">
          <template #icon>
            <icon-check/>
          </template>
          审核
        </a-button>
        <a-button 
          type="primary" 
          @click="previewPdf"
          :disabled="!workOrderData.pdfUrl"
          style="margin-right: 10px;">
          查看PDF
        </a-button>
        <a-button type="primary" @click="generatePdf">
          <template #icon>
            <icon-download/>
          </template>
          重新生成PDF
        </a-button>
      </div>

      <div v-if="workOrderData" class="work-order-info">
        <!-- 基本信息 -->
        <a-card title="基本信息" style="margin-bottom: 16px;">
          <a-descriptions :column="3" layout="horizontal" bordered>
            <a-descriptions-item label="工单编码">
              {{ workOrderData.workorderCode || '—' }}
            </a-descriptions-item>
            <a-descriptions-item label="项目服务单ID">
              {{ getProjectServiceOrderText(workOrderData.projectServiceOrderId) }}
            </a-descriptions-item>
            <a-descriptions-item label="工单类型">
              {{ getWorkorderTypeText(workOrderData.workorderType) }}
            </a-descriptions-item>
<!--            <a-descriptions-item label="任务类型">
              {{ workOrderData.taskType }}
            </a-descriptions-item>-->
            <a-descriptions-item label="工作类别">
              {{ getWorkCategoryText(workOrderData.workCategory) }}
            </a-descriptions-item>
            <a-descriptions-item label="所属门店">
              {{ workOrderData.customerStore.storeName }}
            </a-descriptions-item>
            <a-descriptions-item label="服务人员">
              {{ getServicePersonNames(workOrderData.servicePersonIds) }}
            </a-descriptions-item>
            <a-descriptions-item label="工单状态">
              <a-tag :color="getWorkStatusColor(workOrderData.workStatus)">
                {{ getWorkStatusText(workOrderData.workStatus) }}
              </a-tag>
            </a-descriptions-item>
            <a-descriptions-item label="是否紧急">
              <a-tag :color="workOrderData.urgent ? 'red' : 'blue'">
                {{ workOrderData.urgent ? '紧急' : '普通' }}
              </a-tag>
            </a-descriptions-item>
            <a-descriptions-item label="计划时间段">
              {{ formatDate(workOrderData.serviceTime) || '—' }}
            </a-descriptions-item>
            <a-descriptions-item label="工单开始时间">
              {{ formatDateTime(workOrderData.startTime) }}
            </a-descriptions-item>
            <a-descriptions-item label="工单结束时间">
              {{ formatDateTime(workOrderData.endTime) }}
            </a-descriptions-item>
            <a-descriptions-item label="工作地点" :span="3">
              {{ workOrderData.workLocation || '—' }}
            </a-descriptions-item>
            <a-descriptions-item label="工作内容" :span="3">
              {{ workOrderData.workContent || '—' }}
            </a-descriptions-item>
            <a-descriptions-item label="备注" :span="3">
              {{ workOrderData.remark || '—' }}
            </a-descriptions-item>
          </a-descriptions>
        </a-card>

        <!-- 联系信息 -->
        <a-card title="联系信息" style="margin-bottom: 16px;">
          <a-descriptions :column="3" layout="horizontal" bordered>
            <a-descriptions-item label="联系人">
              {{ workOrderData.contactPerson || '—' }}
            </a-descriptions-item>
            <a-descriptions-item label="联系电话">
              {{ workOrderData.contactPhone || '—' }}
            </a-descriptions-item>

          </a-descriptions>
        </a-card>

        <!-- 签到记录 -->
        <a-card v-if="workOrderData.workorderCheckinRecordList && workOrderData.workorderCheckinRecordList.length > 0"
                title="签到记录" style="margin-bottom: 16px;">
          <!-- 地图视图 -->
          <div style="margin-bottom: 20px;">
            <div style="display: flex; margin-bottom: 10px;">
              <div style="width: 50%;">
                <div class="checkin-map-container square-map">
                  <div id="checkin-map" style="width: 100%; height: 100%;"></div>
                </div>
              </div>
              <div style="width: 50%; padding-left: 10px;">
                <div 
                  v-if="workOrderData.customerStore && workOrderData.customerStore.longitude && workOrderData.customerStore.latitude"
                  style="margin-bottom: 5px;"
                >
                  <a-tag 
                    color="arcoblue" 
                    size="large"
                    clickable
                    @click="panToStore"
                    style="width: 100%; text-align: center;"
                  >
                    门店:{{ workOrderData.customerStore.storeName || '未知门店' }}
                  </a-tag>
                </div>
                <div 
                  v-for="(record, index) in workOrderData.workorderCheckinRecordList.filter(r => r.checkinLongitude && r.checkinLatitude)"
                  :key="index"
                  style="margin-bottom: 5px;"
                >
                  <a-tag 
                    :color="record.invalidStatus === 2 ? 'red' : 'green'" 
                    size="large"
                    clickable
                    @click="panToCheckinPoint(record)"
                    style="width: 100%; text-align: center;"
                  >
                    签到点{{ index + 1 }}:{{ getUserText(record.createBy) || '未知用户' }}
                  </a-tag>
                </div>
              </div>
            </div>
          </div>
          
          <!-- 签到记录表格 -->
          <a-table
              row-key="id"
              :columns="checkinColumns"
              :data="workOrderData.workorderCheckinRecordList"
              :pagination="false"
              column-resizable
              stripe
              :bordered="{cell:true}"
              size="small"
              style="margin-top: 20px;"
          >
            <template #checkinTime="{ record }">
              {{ formatDateTime(record.checkinTime) }}
            </template>
            <template #createBy="{ record }">
              {{ getUserText(record.createBy) }}
            </template>
            <template #checkinLongitude="{ record }">
              {{ record.checkinLongitude }}
            </template>
            <template #checkinLatitude="{ record }">
              {{ record.checkinLatitude }}
            </template>
            <template #checkinAddress="{ record }">
              {{ record.checkinAddress || '—' }}
            </template>
            <template #invalidStatus="{ record }">
              <a-tag v-if="record.invalidStatus === 0" color="gray">未校验</a-tag>
              <a-tag v-else-if="record.invalidStatus === 1" color="green">正常</a-tag>
              <a-tag v-else-if="record.invalidStatus === 2" color="red">异常</a-tag>
              <span v-else>—</span>
            </template>
            <template #workStartTime="{ record }">
              {{ formatDateTime(record.workStartTime) }}
            </template>
            <template #workSubmitTime="{ record }">
              {{ formatDateTime(record.workSubmitTime) }}
            </template>
          </a-table>
        </a-card>

        <!-- 行动项目 -->
        <a-card v-if="workOrderData.workorderActionItemList && workOrderData.workorderActionItemList.length > 0"
                title="行动项目" style="margin-bottom: 16px;">
          <a-table
              row-key="id"
              :columns="actionItemColumns"
              :data="workOrderData.workorderActionItemList"
              :pagination="false"
              column-resizable
              stripe
              :bordered="{cell:true}"
              size="small"
          >
            <template #followFirstTime="{ record }">
              {{ formatDateTime(record.followFirstTime) }}
            </template>
            <template #executeTime="{ record }">
              {{ formatDateTime(record.createTime) }}
            </template>
            <template #status="{ record }">
              <a-tag :color="record.completed ? 'green' : 'red'">
                {{ record.completed ? '已完成' : '未完成' }}
              </a-tag>
            </template>
            <template #photoRecords="{ record }">
              <photo-display :photo-urls="record.photoRecords"/>
            </template>
            <template #createBy="{ record }">
              {{ getUserText(record.createBy) }}
            </template>
          </a-table>
        </a-card>

        <!-- 虫情勘察 -->
        <a-card v-if="workOrderData.workorderPestSurveyList && workOrderData.workorderPestSurveyList.length > 0"
                title="虫情勘察" style="margin-bottom: 16px;">
          <a-table
              row-key="id"
              :columns="pestSurveyColumns"
              :data="workOrderData.workorderPestSurveyList"
              :pagination="false"
              column-resizable
              stripe
              :bordered="{cell:true}"
              size="small"
          >
            <template #surveyTime="{ record }">
              {{ formatDateTime(record.createTime) }}
            </template>
            <template #hasMouseTrace="{ record }">
              <a-tag :color="record.hasMouseTrace ? 'green' : 'red'">{{ record.hasMouseTrace ? "有" : "无" }}</a-tag>
            </template>
            <template #photoRecords="{ record }">
              <photo-display :photo-urls="record.photoRecords"/>
            </template>
            <template #createBy="{ record }">
              {{ getUserText(record.createBy) }}
            </template>
          </a-table>
        </a-card>

        <!-- 设备详情 -->
        <a-card v-if="workOrderData.workorderDeviceDetailList && workOrderData.workorderDeviceDetailList.length > 0"
                title="设备详情" style="margin-bottom: 16px;">
          <a-table
              row-key="id"
              :columns="deviceDetailsColumns"
              :data="workOrderData.workorderDeviceDetailList"
              :pagination="false"
              column-resizable
              stripe
              :bordered="{cell:true}"
              size="small"
          >
            <template #captured="{ record }">
              {{ record.captured ? "是" : "否" }}
            </template>
            <template #installTime="{ record }">
              {{ formatDateTime(record.createTime) }}
            </template>
            <template #status="{ record }">
              <a-tag :color="getEquipmentStatusColor(record.status)">
                {{ getEquipmentStatusText(record.status) }}
              </a-tag>
            </template>
            <template #photoRecords="{ record }">
              <photo-display :photo-urls="record.photoRecords"/>
            </template>
            <template #createBy="{ record }">
              {{ getUserText(record.createBy) }}
            </template>
          </a-table>
        </a-card>

        <!-- 物料汇总 -->
        <a-card
            v-if="workOrderData.workorderMaterialSummaryList && workOrderData.workorderMaterialSummaryList.length > 0"
            title="物料汇总" style="margin-bottom: 16px;">
          <a-table
              row-key="id"
              :columns="materialSummaryColumns"
              :data="workOrderData.workorderMaterialSummaryList"
              :pagination="false"
              column-resizable
              stripe
              :bordered="{cell:true}"
              size="small"
          />
        </a-card>

        <!-- 签字记录 -->
        <a-card
            v-if="workOrderData.workorderSignatureRecordList && workOrderData.workorderSignatureRecordList.length > 0"
            title="签字记录" style="margin-bottom: 16px;">
          <a-table
              row-key="id"
              :columns="signatureColumns"
              :data="workOrderData.workorderSignatureRecordList"
              :pagination="false"
              column-resizable
              stripe
              :bordered="{cell:true}"
              size="small"
          >
            <template #signatureTime="{ record }">
              {{ formatDateTime(record.signatureTime) }}
            </template>
            <template #signerType="{ record }">
              {{ getSignerTypeText(record.signerType) }}
            </template>
            <template #signatureContent="{ record }">
              <photo-display :photo-urls="record.signatureContent"/>
            </template>
            <template #signatureEvaluation="{ record }">
              <a-rate v-if="record.signerType==='customer'" :model-value="record.signatureEvaluation" readonly allow-half />
            </template>
          </a-table>
        </a-card>

        <!-- 审批记录 -->
        <a-card v-if="workOrderData.workorderApprovalRecordList && workOrderData.workorderApprovalRecordList.length > 0"
                title="审批记录" style="margin-bottom: 16px;">
          <a-table
              row-key="id"
              :columns="approvalColumns"
              :data="workOrderData.workorderApprovalRecordList"
              :pagination="false"
              column-resizable
              stripe
              :bordered="{cell:true}"
              size="small"
          >
            <template #approvalTime="{ record }">
              {{ formatDate(record.approvalTime) }}
            </template>
            <template #approver="{ record }">
              {{ getUserText(record.approver) }}
            </template>
            <template #approvalStatus="{ record }">
              <a-tag :color="getApprovalStatusColor(record.approvalStatus)">
                {{ getApprovalStatusText(record.approvalStatus) }}
              </a-tag>
            </template>
          </a-table>
        </a-card>

        <!-- 扩展信息记录 -->
        <a-card v-if="workOrderData.workorderExtensionInfoList && workOrderData.workorderExtensionInfoList.length > 0"
                title="扩展信息记录" style="margin-bottom: 16px;">
          <a-table
              row-key="id"
              :columns="extensionInfoColumns"
              :data="workOrderData.workorderExtensionInfoList"
              :pagination="false"
              column-resizable
              stripe
              :bordered="{cell:true}"
              size="small"
          >
<!--            <template #status="{ record }">
              <a-tag :color="record.status ? 'green' : 'red'">
                {{ record.status ? '启用' : '禁用' }}
              </a-tag>
            </template>-->
            <template #isRequired="{ record }">
              <a-tag :color="record.isRequired ? 'orange' : 'blue'">
                {{ record.isRequired ? '必填' : '选填' }}
              </a-tag>
            </template>
            <template #storeCustomValue="{ record }">
              <template v-if="record.optionType === '图片'">
                <photo-display :photo-urls="record.storeCustomValue"/>
              </template>
              <template v-else>
                {{ record.storeCustomValue }}
              </template>
            </template>
          </a-table>
        </a-card>
      </div>

      <!-- 加载状态 -->
      <div v-else-if="loading" class="loading-container">
        <a-spin/>
        <p>正在加载工单详情...</p>
      </div>

      <!-- 无数据状态 -->
      <div v-else class="no-data-container">
        <a-empty description="暂无数据"/>
      </div>
    </div>
  </a-modal>
  
  <!-- 审批模态框 -->
  <approval-modal
    v-model="isShowApproveWorkOrderModal"
    :work-order-id="workOrderId"
    @success="handleApprovalSuccess"
  />
  
  <!-- 编辑模态框 -->
  <sub-table-edit
    v-model="isShowEditWorkOrderModal"
    :work-order-id="workOrderId"
    @success="handleEditSuccess"
  />
  
  <!-- PDF预览模态框 -->
  <!-- 已移除PDF预览模态框，改为在新窗口中打开PDF -->
  

</template>

<script setup>
import {ref, watch, onMounted, nextTick} from 'vue'
import {Message} from "@arco-design/web-vue"
import {getWorkOrderInfo, genWorkOrderPdf} from '@/apis/work-order-api'
import PhotoDisplay from './PhotoDisplay.vue'
import ApprovalModal from './ApprovalModal.vue'
import SubTableEdit from './SubTableEdit.vue'
import {timestampNumber2dateString, timestampNumber2datetimeString} from "@/utils/date-util"
import {useSysDictDataStore} from "@/store/system-data"
import {useCustomerStoreInfoStore} from "@/store/customer-data"
import {useProjectServiceOrderStore} from "@/store/work-order-data"
import {useUserDataStore} from "@/store/user-data"
import {IconDownload, IconCheck, IconEdit, IconLocation} from '@arco-design/web-vue/es/icon';

// 定义 props
const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  workOrderId: {
    type: [String, Number],
    default: null
  }
})

// 定义 emits
const emit = defineEmits(['update:modelValue'])

// store
// 数据字典
const sysDictDataStore = useSysDictDataStore();
// 客户门店信息
const customerStoreInfoStore = useCustomerStoreInfoStore();
// 项目服务单
const projectServiceOrderStore = useProjectServiceOrderStore();
// 用户数据
const userDataStore = useUserDataStore();

const formatDateTime = (dateString) => {
  // if (!dateString) return '-'
  // const date = new Date(dateString)
  // return date.toLocaleString('zh-CN')
  if (!dateString) return '—';
  // return dateString.substring(0, 16).replace('T', ' ');
  return timestampNumber2datetimeString(dateString)
}
// 响应式数据
const visible = ref(false)
const loading = ref(false)
const workOrderData = ref(null)

// 新增响应式数据
const pdfUrl = ref('')
const pdfGenerating = ref(false)
// 审批模态框显示状态
const isShowApproveWorkOrderModal = ref(false)
// 编辑模态框显示状态
const isShowEditWorkOrderModal = ref(false)

// 签到记录
const checkinColumns = ref([
  {title: '签到时间', dataIndex: 'checkinTime', width: 160, slotName: 'checkinTime'},
  {title: '签到人', dataIndex: 'createBy', width: 120, slotName: 'createBy'},
  {title: '签到经度', dataIndex: 'checkinLongitude', width: 120, slotName: 'checkinLongitude'},
  {title: '签到纬度', dataIndex: 'checkinLatitude', width: 120, slotName: 'checkinLatitude'},
  // {title: '签到地址', dataIndex: 'checkinAddress', width: 200, slotName: 'checkinAddress'},
  {title: '校验状态', dataIndex: 'invalidStatus', width: 120, slotName: 'invalidStatus'},
  {title: '开始作业时间', dataIndex: 'workStartTime', width: 160, slotName: 'workStartTime'},
  {title: '作业提交时间', dataIndex: 'workSubmitTime', width: 160, slotName: 'workSubmitTime'}
])
//行动项目
const actionItemColumns = ref([
  {title: '首次发现时间', dataIndex: 'followFirstTime', width: 160, slotName: 'followFirstTime'},
  {title: '情况类型', dataIndex: 'caseTypeName', width: 100},
  {title: '区域', dataIndex: 'regionName', width: 80},
  {title: '区域详情', dataIndex: 'regionText', width: 80},
  {title: '风险类型', dataIndex: 'caseDescriptionNames', width: 180, ellipsis: true, tooltip: true},
  {title: '风险描述', dataIndex: 'caseDescriptionText', width: 180, ellipsis: true, tooltip: true},
  {title: '建议措施', dataIndex: 'suggestionMeasureName', width: 180, ellipsis: true, tooltip: true},
  {title: '措施补充说明', dataIndex: 'suggestionMeasureText', width: 180, ellipsis: true, tooltip: true},
  {title: '跟进方', dataIndex: 'followerName', width: 100},
  {title: '跟进次数', dataIndex: 'followUpCount', width: 80},
  {title: '备注', dataIndex: 'remark', width: 100},
  {title: '创建人', dataIndex: 'createBy', width: 100, slotName: 'createBy'},
  {title: '创建时间', dataIndex: 'createTime', width: 160, slotName: 'executeTime'},
  {title: '是否完成', dataIndex: 'completed', width: 100, slotName: 'status'},
  {title: '照片记录', dataIndex: 'photoRecords', width: 150, slotName: 'photoRecords'}
])
// 虫害勘察
const pestSurveyColumns = ref([
  {title: '勘察区域', dataIndex: 'regionName', width: 120},
  {title: '鼠类数量', dataIndex: 'mouseCount', width: 100},
  {title: '有无鼠迹', dataIndex: 'hasMouseTrace', width: 100, slotName: 'hasMouseTrace'},
  {title: '鼠类详情', dataIndex: 'mouseDetails', width: 150, ellipsis: true, tooltip: true},
  {title: '蟑螂数量', dataIndex: 'cockroachCount', width: 100},
  {title: '蟑螂详情', dataIndex: 'cockroachDetails', width: 150, ellipsis: true, tooltip: true},
  {title: '飞虫数量', dataIndex: 'flyingInsectCount', width: 100},
  {title: '创建人', dataIndex: 'createBy', width: 100, slotName: 'createBy'},
  {title: '创建时间', dataIndex: 'createTime', width: 160, slotName: 'surveyTime'},
  {title: '备注', dataIndex: 'remark', width: 200, ellipsis: true, tooltip: true}
])
//设备详情
const deviceDetailsColumns = ref([
  {title: '区域名称', dataIndex: 'regionName', width: 120},
  {title: '设备位置', dataIndex: 'deviceLocationName', width: 120},
  {title: '位置描述', dataIndex: 'deviceLocationText', width: 120},
  {title: '设备类型', dataIndex: 'deviceTypeName', width: 120},
  {title: '设备状况', dataIndex: 'deviceConditionName', width: 120},
  {title: '是否捕获', dataIndex: 'captured', width: 100, slotName: 'captured'},
  {title: '捕获数量', dataIndex: 'captureCount', width: 100},
  {title: '创建人', dataIndex: 'createBy', width: 100, slotName: 'createBy'},
  {title: '创建时间', dataIndex: 'createTime', width: 160, slotName: 'installTime'},
  {title: '备注', dataIndex: 'remark', width: 200, ellipsis: true, tooltip: true},
  {title: '照片记录', dataIndex: 'photoRecords', width: 150, slotName: 'photoRecords'}
])
// 物料使用记录
const materialSummaryColumns = ref([
  {title: '害虫类型', dataIndex: 'pestTypeName', width: 100},
  {title: '物料名称', dataIndex: 'materialName', width: 120},
  {title: '稀释比例', dataIndex: 'dilutionRatio', width: 100},
  {title: '计量单位', dataIndex: 'unit', width: 100},
  {title: '使用数量', dataIndex: 'materialQuantity', width: 100},
  {title: '使用方法', dataIndex: 'usageMethod', width: 120},
  {title: '备注', dataIndex: 'remark', width: 200, ellipsis: true, tooltip: true}
])
// 签字记录
const signatureColumns = ref([
  {title: '签字人类型', dataIndex: 'signerType', width: 100, slotName: 'signerType'},
  {title: '签字人姓名', dataIndex: 'signerName', width: 100},
  {title: '签字时间', dataIndex: 'signatureTime', width: 160, slotName: 'signatureTime'},
  {
    title: '签字内容',
    dataIndex: 'signatureContent',
    width: 50,
    slotName: 'signatureContent',
    ellipsis: true,
    tooltip: true
  },
  // { title: '签字评分', dataIndex: 'signatureEvaluation', width: 130, slotName: 'signatureEvaluation' },
  {title: '签字评分', dataIndex: 'signatureEvaluation', width: 100, slotName: 'signatureEvaluation'},
  {title: '评价详情', dataIndex: 'evaluationDetail', width: 200, ellipsis: true, tooltip: true}
])
// 审批记录
const approvalColumns = ref([
  {title: '审批时间', dataIndex: 'approvalTime', width: 160, slotName: 'approvalTime'},
  {title: '审批人', dataIndex: 'approver', width: 100, slotName: 'approver'},
  {title: '审批状态', dataIndex: 'approvalStatus', width: 100, slotName: 'approvalStatus'},
  {title: '审批意见', dataIndex: 'approvalResult', width: 200, ellipsis: true, tooltip: true}
])
//扩展字段
const extensionInfoColumns = ref([
  {title: '选项标签', dataIndex: 'optionLabel', width: 80},
  {title: '选项名称', dataIndex: 'remark', width: 500},
  {title: '选项类型', dataIndex: 'optionType', width: 80},
  // {title: '默认值', dataIndex: 'defaultValue', width: 100},
  // {title: '是否必填', dataIndex: 'required', width: 100, slotName: 'isRequired'},
  // {title: '状态', dataIndex: 'status', width: 80, slotName: 'status'},
  {title: '扩展字段值', dataIndex: 'storeCustomValue', width: 80, slotName: 'storeCustomValue'}
])

// 监听 modelValue 变化
watch(() => props.modelValue, (newVal) => {
  visible.value = newVal
  if (newVal && props.workOrderId) {
    fetchWorkOrderData()
  }
})

// 监听 visible 变化
watch(visible, (newVal) => {
  emit('update:modelValue', newVal)
})

// 监听 workOrderData 变化，当数据加载完成后初始化地图
watch(workOrderData, (newVal) => {
  if (newVal && visible.value) {
    nextTick(() => {
      initCheckinMap();
    });
  }
});

// 初始化签到地图
const initCheckinMap = () => {
  // 确保地图容器存在
  const mapContainer = document.getElementById('checkin-map');
  if (!mapContainer) {
    console.warn('Map container not found');
    return;
  }

  // 清空容器
  mapContainer.innerHTML = '';

  // 动态加载高德地图 JS API
  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.src = 'https://webapi.amap.com/maps?v=1.4.15&key=3535037ccbea350d733f8860f6604110';
  script.onload = () => {
    renderCheckinMap();
  };
  script.onerror = () => {
    console.error('Failed to load AMap API');
    mapContainer.innerHTML = '<div style="display:flex;align-items:center;justify-content:center;height:100%;color:#999;">地图加载失败</div>';
  };
  document.head.appendChild(script);
};

// 添加响应式变量用于保存地图实例
const checkinMap = ref(null);

// 导航到门店位置
const panToStore = () => {
  if (!checkinMap.value) return;
  
  const storeData = workOrderData.value?.customerStore;
  const storeLatitude = storeData?.latitude;
  const storeLongitude = storeData?.longitude;
  
  if (storeLongitude && storeLatitude) {
    checkinMap.value.setCenter([storeLongitude, storeLatitude]);
    checkinMap.value.setZoom(16);
  }
};

// 导航到签到点位置
const panToCheckinPoint = (record) => {
  if (!checkinMap.value || !record) return;
  
  if (record.checkinLongitude && record.checkinLatitude) {
    checkinMap.value.setCenter([record.checkinLongitude, record.checkinLatitude]);
    checkinMap.value.setZoom(16);
  }
};

// 渲染签到地图
const renderCheckinMap = () => {
  const mapContainer = document.getElementById('checkin-map');
  if (!mapContainer || !window.AMap) {
    console.warn('AMap not available');
    return;
  }

  // 获取门店坐标
  const storeData = workOrderData.value?.customerStore;
  const storeLatitude = storeData?.latitude;
  const storeLongitude = storeData?.longitude;
  // 获取门店名称
  const storeName = storeData?.storeName || '未知门店';

  // 收集所有需要显示的点位坐标
  const allPoints = [];
  
  // 添加所有签到点
  workOrderData.value.workorderCheckinRecordList.forEach((record) => {
    if (record.checkinLongitude && record.checkinLatitude) {
      allPoints.push([record.checkinLongitude, record.checkinLatitude]);
    }
  });

  // 添加门店位置（如果存在）
  if (storeLongitude && storeLatitude) {
    allPoints.push([storeLongitude, storeLatitude]);
  }

  // 设置地图中心点
  let center = [116.397428, 39.90923]; // 默认北京天安门
  if (allPoints.length > 0) {
    // 计算所有点的中心点
    let lonSum = 0, latSum = 0;
    allPoints.forEach(point => {
      lonSum += point[0];
      latSum += point[1];
    });
    center = [lonSum / allPoints.length, latSum / allPoints.length];
  }

  // 创建地图
  const map = new window.AMap.Map('checkin-map', {
    zoom: allPoints.length > 0 ? 14 : 4, // 如果有数据则使用较高缩放级别
    center: center,
    resizeEnable: true
  });
  
  // 保存地图实例
  checkinMap.value = map;

  // 添加签到点标记
  workOrderData.value.workorderCheckinRecordList.forEach((record, index) => {
    if (record.checkinLongitude && record.checkinLatitude) {
      // 获取签到人名称
      const checkinPerson = getUserText(record.createBy) || '未知用户';
      const checkinTime = formatDateTime(record.checkinTime);
      
      // 创建标记
      const marker = new window.AMap.Marker({
        position: [record.checkinLongitude, record.checkinLatitude],
        title: `${checkinPerson}-${checkinTime}`,
        label: {
          content: `${checkinPerson}`,
          offset: new window.AMap.Pixel(0, 0)
        }
      });
      
      // 添加信息窗口
      const infoWindow = new window.AMap.InfoWindow({
        content: `<div style="padding: 5px;">
                    <div><strong>${checkinPerson}</strong></div>
                    <div>时间: ${checkinTime}</div>
                    <div>坐标: ${record.checkinLongitude}, ${record.checkinLatitude}</div>
                  </div>`,
        offset: new window.AMap.Pixel(0, -20)
      });
      
      // 绑定点击事件
      marker.on('click', () => {
        infoWindow.open(map, marker.getPosition());
      });
      
      // 添加到地图
      map.add(marker);
    }
  });

  // 添加门店标记和范围圆圈
  if (storeLongitude && storeLatitude) {
    // 门店标记
    const storeMarker = new window.AMap.Marker({
      position: [storeLongitude, storeLatitude],
      title: `门店:${storeName}`,
      label: {
        content: `门店:${storeName}`,
        offset: new window.AMap.Pixel(0, 0)
      },
      icon: new window.AMap.Icon({
        size: new window.AMap.Size(32, 32),
        image: '//vdata.amap.com/icons/b18/1/2.png',
        imageSize: new window.AMap.Size(32, 32)
      })
    });
    
    // 添加信息窗口
    const storeInfoWindow = new window.AMap.InfoWindow({
      content: `<div style="padding: 5px;">
                  <div><strong>门店:${storeName}</strong></div>
                  <div>坐标: ${storeLongitude}, ${storeLatitude}</div>
                </div>`,
      offset: new window.AMap.Pixel(0, -20)
    });
    
    // 绑定点击事件
    storeMarker.on('click', () => {
      storeInfoWindow.open(map, storeMarker.getPosition());
    });
    
    // 添加到地图
    map.add(storeMarker);
    
    // 500米范围圆圈
    const circle = new window.AMap.Circle({
      center: [storeLongitude, storeLatitude],
      radius: 500,
      fillOpacity: 0.2,
      fillColor: '#1890FF',
      strokeColor: '#1890FF',
      strokeWeight: 1
    });
    
    // 添加到地图
    map.add(circle);
  }
  
  // 调整地图视野以包含所有元素
  if (allPoints.length > 1) {
    map.setFitView();
  } else if (allPoints.length === 1) {
    map.setCenter(allPoints[0]);
  }
};

// 获取工单详情数据
const fetchWorkOrderData = async () => {
  if (!props.workOrderId) {
    Message.warning('项目工单ID不能为空')
    return
  }

  loading.value = true
  try {
    const response = await getWorkOrderInfo(props.workOrderId)
    if (response.code === 0) {
      workOrderData.value = response.data
    } else {
      Message.error(response.message || '获取项目工单详情失败')
      console.error('获取项目工单详情失败:', response)
    }
  } catch (error) {
    Message.error(error.message || '获取项目工单详情失败')
    console.error('获取项目工单详情失败:', error)
  } finally {
    loading.value = false
  }
}

// 关闭弹框
const handleCancel = () => {
  visible.value = false
  workOrderData.value = null
}

// 格式化方法
const formatDate = (dateString) => {
  if (!dateString) return '—'
  // return dateString.substring(0, 16).replace('T', ' ');
  return timestampNumber2dateString(dateString)
}

const formatDuration = (minutes) => {
  if (!minutes) return '—';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return hours > 0 ? `${hours}小时${mins}分钟` : `${mins}分钟`;
}

// 工单状态
const getWorkStatusText = (status) => {
  const dictItem = sysDictDataStore.workOrderInfoWorkStatusMap.get(status + "");
  return dictItem ? dictItem.dictLabel : '未知';
}

const getWorkStatusColor = (status) => {
  // 颜色 可以根据需要调整
  const colorMap = {'1': 'blue', '2': 'cyan', '3': 'orange', '4': 'green', '5': 'red'}
  return colorMap[status] || 'gray'
}

// 工单类型
const getWorkorderTypeText = (workorderType) => {
  const dictItem = sysDictDataStore.workOrderInfoWorkorderTypeMap.get(workorderType + "");
  return dictItem ? dictItem.dictLabel : '未知';
}

// 任务类型
const getTaskTypeText = (taskType) => {
  const dictItem = sysDictDataStore.workOrderInfoTaskTypeMap.get(taskType + "");
  return dictItem ? dictItem.dictLabel : '未知';
}

// 工作类别
const getWorkCategoryText = (workCategory) => {
  const dictItem = sysDictDataStore.workOrderInfoWorkCategoryMap.get(workCategory + "");
  return dictItem ? dictItem.dictLabel : '未知';
}

// 所属门店
const getServiceDoorText = (storeId) => {
  const store = customerStoreInfoStore.customerStoreIdMap.get(storeId);
  return store ? store.storeName : storeId || '—';
}

// 项目服务单
const getProjectServiceOrderText = (projectServiceOrderId) => {
  const serviceOrder = projectServiceOrderStore.projectServiceOrderDataIdMap.get(projectServiceOrderId + "");
  return serviceOrder ? `${serviceOrder.id}-${serviceOrder.serviceContent || ''}` : projectServiceOrderId || '—';
}

// 设备状态文本获取函数
const getEquipmentStatusText = (status) => {
  const dictItem = sysDictDataStore.equipmentStatusMap.get(status + "");
  return dictItem ? dictItem.dictLabel : '未知';
}

// 设备状态颜色获取函数
const getEquipmentStatusColor = (status) => {
  const colorMap = {1: 'green', 2: 'orange', 3: 'red', 4: 'gray'}
  return colorMap[status] || 'gray'
}

// 审批状态
const getApprovalStatusText = (status) => {
  const dictItem = sysDictDataStore.workOrderApprovalStatusMap.get(status + "");
  return dictItem ? dictItem.dictLabel : '待审批';
}

const getApprovalStatusColor = (status) => {
  // 颜色 可以根据需要调整
  const colorMap = {1: 'gray', 2: 'orange', 3: 'green', 4: 'red'}
  return colorMap[status] || 'gray'
}

// 用户名称获取函数（支持逗号分隔的多个用户ID）
const getUserText = (userId) => {
  if (!userId) return '—';

  // 如果是逗号分隔的多个用户ID
  if (userId.includes(',')) {
    const userIds = userId.split(',');
    const userNames = userIds.map(id => {
      const user = userDataStore.userIdMap.get(id.trim());
      return user ? (user.nickname || user.username) : id.trim();
    });
    return userNames.join(', ');
  }

  // 单个用户ID
  const user = userDataStore.userIdMap.get(userId + "");
  return user ? (user.nickname || user.username) : userId || '—';
}

// 获取服务人员名称（支持逗号分隔的多个用户ID）
const getServicePersonNames = (servicePersonIds) => {
  return getUserText(servicePersonIds);
}

// 获取签字人类型文本
const getSignerTypeText = (signerType) => {
  const signerTypeMap = {
    'employee': '技术人员',
    'customer': '客户'
  };
  return signerTypeMap[signerType] || signerType || '—';
}

// 生成PDF
const generatePdf = async () => {
  try {
    const response = await genWorkOrderPdf(props.workOrderId);
    if (response.code === 0) {
      // 在新窗口中打开PDF而不是在模态框中预览
      if (response.data) {
        window.open(response.data, '_blank');
      }
      // 同时更新工单数据中的pdfUrl
      if (workOrderData.value) {
        workOrderData.value.pdfUrl = response.data;
      }
      Message.success('PDF生成成功')
    } else {
      Message.error(response.message || 'PDF生成失败')
    }
  } catch (error) {
    Message.error('PDF生成失败: ' + (error.message || '未知错误'))
    console.error('PDF生成失败:', error)
  }
}

// 为预览生成PDF的方法
const generatePdfForPreview = async () => {
  if (!props.workOrderId) {
    Message.error('工单ID不能为空')
    return
  }
  
  pdfGenerating.value = true
  try {
    const response = await genWorkOrderPdf(props.workOrderId)
    if (response.code === 0) {
      // 添加时间戳防止缓存
      pdfUrl.value = response.data + (response.data.includes('?') ? '&' : '?') + '_t=' + new Date().getTime()
      // 同时更新工单数据中的pdfUrl
      if (workOrderData.value) {
        workOrderData.value.pdfUrl = response.data
      }
      Message.success('PDF生成成功')
      if (workOrderData.value && workOrderData.value.pdfUrl) {
        window.open(workOrderData.value.pdfUrl, '_blank');
      } else {
        Message.warning('暂无PDF文件');
      }
    } else {
      Message.error(response.message || 'PDF生成失败')
    }
  } catch (error) {
    Message.error('PDF生成失败: ' + (error.message || '未知错误'))
    console.error('PDF生成失败:', error)
  } finally {
    pdfGenerating.value = false
  }
}

// 显示审批模态框
const showApprovalModal = () => {
  isShowApproveWorkOrderModal.value = true
}

// 审批按钮是否禁用
const approveButtonStatus = (record) => {
  // 工单状态不是"已完成"时禁用审批按钮
  return record?.workStatus !== "3"
}

// 审批成功回调
const handleApprovalSuccess = () => {
  // 重新获取工单数据以更新审批记录和状态
  fetchWorkOrderData()
}

// 显示编辑模态框
const showEditModal = () => {
  isShowEditWorkOrderModal.value = true
}

// 编辑成功回调
const handleEditSuccess = () => {
  // 重新获取工单数据以更新子表信息
  fetchWorkOrderData()
}

// 显示PDF预览
const previewPdf = () => {
  if (workOrderData.value && workOrderData.value.pdfUrl) {
    // 在新窗口中打开PDF
    window.open(workOrderData.value.pdfUrl, '_blank');
  } else {
    Message.warning('暂无PDF文件')
  }
}

</script>

<style scoped>
.work-order-info-modal-content {
  padding: 16px 0;
}

.modal-header-actions {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;
}

.loading-container {
  text-align: center;
  padding: 50px 0;
}

.loading-container p {
  margin-top: 16px;
  color: #666;
}

.no-data-container {
  text-align: center;
  padding: 50px 0;
}

.work-order-info {
  max-height: 600px;
  overflow-y: auto;
}

:deep(.arco-descriptions-item-label) {
  font-weight: 500;
  color: #1890ff;
}

.checkin-map-container {
  position: relative;
  width: 100%;
  height: 400px;
}

.checkin-map-container.square-map {
  height: 0;
  padding-bottom: 50%;
}

.checkin-map-container.square-map #checkin-map {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.map-marker {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -100%);
  font-size: 24px;
  z-index: 10;
  pointer-events: none;
}
</style>